import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegHeart, FaHeart, FaStar, FaRegStar } from "react-icons/fa";
import React from "react";

import styles from "./OutfitCard.module.css";
import {
    HunterInteraction,
    HunterInteractionType,
    HunterOutfit,
} from "@mythic/coms/types";
import TagList from "./TagList";

interface Props {
    outfit: HunterOutfit & {
        _id?: string;
        isFavorited?: boolean;
        isLiked?: boolean;
        likeCount?: number;
        favCount?: number;
    };
    small?: boolean;
    onInteract: (interaction: HunterInteractionType) => void;
}

export default function OutfitCard({ outfit, small, onInteract }: Props) {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);
    const isPendingRef = useRef(false);

    const bodyRef = useRef<HTMLDivElement | null>(null);

    const handleInteract = (ev: any, interaction: HunterInteractionType) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (isPendingRef.current) return;
        isPendingRef.current = true;

        fetch(
            `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/${outfit._id}/interact?action=${interaction}`,
            {
                credentials: "include",
            },
        )
            .then((res) => res.json())
            .then((data) => {
                if (data.ok) onInteract(interaction);
            })
            .finally(() => {
                isPendingRef.current = false;
            });
    };

    return (
        <div
            ref={bodyRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => {
                if (outfit._id) navigate(`/hunterfashion/outfit/${outfit._id}`);
            }}
            className={`${styles.outfitCard} ${small ? styles.small : ""}`}
            style={
                { "--outfit-accent": outfit.accentColor } as React.CSSProperties
            }
        >
            <img
                className={styles.outfitImage}
                style={{ opacity: hovered && outfit.images.hover ? 0 : 1 }}
                src={`${import.meta.env.VITE_SERVER_URL}${outfit.images.cover}`}
            />
            <img
                className={`${styles.outfitImage} ${styles.hoverImage}`}
                style={{ opacity: hovered && outfit.images.hover ? 1 : 0 }}
                src={`${import.meta.env.VITE_SERVER_URL}${outfit.images.hover}`}
            />
            <div className={`${styles.outfitHeader}`}>{outfit.name}</div>
            <div
                className={`${styles.outfitStats} ${hovered && outfit.tags.length > 0 ? styles.hovered : ""}`}
            >
                {outfit.isLiked ? (
                    <FaHeart
                        style={{ color: "red", cursor: "pointer" }}
                        onClick={(ev) => handleInteract(ev, "unlike")}
                    />
                ) : (
                    <FaRegHeart
                        style={{
                            filter: "drop-shadow(0 0 2px red)",
                            cursor: "pointer",
                        }}
                        onClick={(ev) => handleInteract(ev, "like")}
                    />
                )}
                <span>{outfit.likeCount ?? 0}</span>
                <div style={{ display: "inline-block", width: "0.5rem" }}></div>
                {outfit.isFavorited ? (
                    <FaStar
                        style={{ color: "gold", cursor: "pointer" }}
                        onClick={(ev) => handleInteract(ev, "unfavorite")}
                    />
                ) : (
                    <FaRegStar
                        style={{
                            filter: "drop-shadow(0 0 3px gold)",
                            cursor: "pointer",
                        }}
                        onClick={(ev) => handleInteract(ev, "favorite")}
                    />
                )}
                <span>{outfit.favCount ?? 0}</span>
            </div>
            <div
                className={`${styles.tagRow} ${hovered && outfit.tags.length > 0 ? styles.hovered : ""}`}
            >
                <TagList accentColor={outfit.accentColor} tags={outfit.tags} />
            </div>
        </div>
    );
}
